import { useIsAuthenticated } from '@heidi/auth/state';
import { useQuery } from '@tanstack/react-query';
import type { PatientDocumentWithDownloadUrlResponse } from '../../contracts';
import { GET_PATIENT_DOCUMENT_DOWNLOAD_URL } from '../../contracts';
import { getPatientDocumentWithDownloadUrl } from '../api/patients';

/**
 * Fetch a patient document's metadata plus a short-lived presigned S3 URL.
 *
 * Used by the preview dialog. The URL expires (BE returns
 * `download_url_expires_at`), so we keep `staleTime` under the BE's 15-min
 * expiry so a lingering dialog re-signs rather than 403s.
 */
export function useQueryPatientDocumentDownloadUrl(
  patientProfileId: string | null | undefined,
  documentId: string | null | undefined,
  options?: { enabled?: boolean },
  /** Extra caller gate. Defaults open; the auth seam is read here. */
  isAuthenticated = true
) {
  const { isAuthenticated: authed } = useIsAuthenticated();

  return useQuery<PatientDocumentWithDownloadUrlResponse>({
    queryKey: [GET_PATIENT_DOCUMENT_DOWNLOAD_URL, { patientProfileId, documentId }],
    enabled:
      authed &&
      isAuthenticated &&
      !!patientProfileId &&
      !!documentId &&
      (options?.enabled ?? true),
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      if (!patientProfileId || !documentId) {
        throw new Error('Missing patientProfileId or documentId');
      }
      const { data } = await getPatientDocumentWithDownloadUrl(
        patientProfileId,
        documentId
      );
      return data;
    },
  });
}
